import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text, Spinner, Button } from "tamagui";
import { RiArrowLeftSLine, RiCheckLine } from "react-icons/ri";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { useCusAuth } from "@utils/store/auth/useCusAuth";

import ListContainer from "../ui/ListContainer";
import { handleAPIListCardPayments, type CardPaymentItem } from "../../API/handlePaymentAPI";

const PAGE_SIZE = 20;
const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * 單卡全部扣款紀錄頁（付款方式詳細頁「最近扣款 → 查看全部」進入）。
 * GET /customer/payment-methods/:id/payments 分頁載入，「載入更多」累加。
 */
export const CardPaymentsAll = () => {
  const navigate = useNavigate();
  const { methodId } = useParams();
  const { cusJwtToken } = useCusAuth();

  const [items, setItems] = useState<CardPaymentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = (nextPage: number) => {
    if (!methodId || !cusJwtToken) return;
    const setBusy = nextPage === 1 ? setLoading : setLoadingMore;
    setBusy(true);
    handleAPIListCardPayments(methodId, cusJwtToken, nextPage, PAGE_SIZE)
      .then((r) => {
        if (r.state !== "success") return;
        setItems((prev) => (nextPage === 1 ? r.items : [...prev, ...r.items]));
        setTotal(r.total);
        setPage(nextPage);
      })
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    load(1);
  }, [methodId, cusJwtToken]);

  const hasMore = items.length < total;
  const back = () => navigate(-1);

  const fmtDateTime = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  };

  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      <Header>
        <XStack ai="center" onPress={back} cursor="pointer">
          <View mr="$2">
            <RiArrowLeftSLine size={20} color={colors.light} />
          </View>
          <Text color={colors.light} fontWeight="bold" fontSize={16}>
            扣款紀錄
          </Text>
        </XStack>
      </Header>
      <RadiusContentWrapper>
        {loading ? (
          <YStack ai="center" py="$8">
            <Spinner size="large" color={colors.primary} />
          </YStack>
        ) : (
          <YStack gap="$3" pb="$6">
            <Text fontSize={12} color={colors.sub}>
              共 {total} 筆
            </Text>
            <ListContainer>
              {items.length === 0 ? (
                <Text fontSize={13} color={colors.hint} ta="center" py="$6">
                  還沒有扣款紀錄
                </Text>
              ) : (
                items.map((tx, i) => (
                  <XStack key={tx.payment_transaction_id} ai="center" gap="$3" px="$3.5" py="$3" bbw={i === items.length - 1 ? 0 : 1} bbc={colors.borderLight}>
                    <View w={36} h={36} br={9} bg={`${colors.primary}15`} ai="center" jc="center" flexShrink={0}>
                      <RiCheckLine size={16} color={colors.primary} />
                    </View>
                    <YStack f={1} miw={0}>
                      <Text fontSize={13} fontWeight="600" color={colors.text}>
                        {tx.card_label ?? "扣款"}
                      </Text>
                      <Text fontSize={11} color={colors.hint} mt="$0.5">
                        {tx.display_id ? `訂單 ${tx.display_id}` : "訂單"} · {fmtDateTime(tx.created_at)}
                      </Text>
                    </YStack>
                    <YStack ai="flex-end">
                      <Text fontSize={14} fontWeight="800" color={colors.text} style={{ fontVariantNumeric: "tabular-nums" }}>
                        ${(tx.amount ?? 0).toLocaleString()}
                      </Text>
                      <Text fontSize={10} color={colors.hint}>
                        {tx.status}
                      </Text>
                    </YStack>
                  </XStack>
                ))
              )}
            </ListContainer>
            {hasMore && (
              <Button
                bg={colors.light}
                borderWidth={1}
                borderColor={colors.gray100}
                br={12}
                h={44}
                disabled={loadingMore}
                onPress={() => load(page + 1)}>
                {loadingMore ? <Spinner size="small" color={colors.primary} /> : <Text fontWeight="600">載入更多</Text>}
              </Button>
            )}
          </YStack>
        )}
      </RadiusContentWrapper>
    </YStack>
  );
};

export default CardPaymentsAll;
